import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { 
  SettingsState, 
  SettingsContextType, 
  SystemSetting, 
  ContactInformation, 
  BankInformation, 
  UserRole, 
  CommunicationSetting, 
  LegalDocument 
} from '../types/settings';

const initialState: SettingsState = {
  systemSettings: [],
  contactInformation: [],
  bankInformation: [],
  userRoles: [],
  communicationSettings: [],
  legalDocuments: [],
  loading: true,
  error: null
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SettingsState>(initialState);

  useEffect(() => {
    refreshSettings();
  }, []);

  const refreshSettings = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      // Fetch all settings data in parallel
      const [
        { data: systemSettings, error: systemError },
        { data: contactInformation, error: contactError },
        { data: bankInformation, error: bankError },
        { data: userRoles, error: rolesError },
        { data: communicationSettings, error: communicationError },
        { data: legalDocuments, error: legalError }
      ] = await Promise.all([
        supabase.from('system_settings').select('*'),
        supabase.from('contact_information').select('*'),
        supabase.from('bank_information').select('*'),
        supabase.from('user_roles').select('*'),
        supabase.from('communication_settings').select('*'),
        supabase.from('legal_documents').select('*')
      ]);

      // Check for errors
      if (systemError) throw systemError;
      if (contactError) throw contactError;
      if (bankError) throw bankError;
      if (rolesError) throw rolesError;
      if (communicationError) throw communicationError;
      if (legalError) throw legalError;

      // Update state with fetched data
      setState({
        systemSettings: systemSettings || [],
        contactInformation: contactInformation || [],
        bankInformation: bankInformation || [],
        userRoles: userRoles || [],
        communicationSettings: communicationSettings || [],
        legalDocuments: legalDocuments || [],
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : 'An error occurred while fetching settings' 
      }));
      toast.error('Failed to load settings');
    }
  };

  // System Settings CRUD operations
  const updateSystemSetting = async (setting: SystemSetting) => {
    try {
      const { error } = await supabase
        .from('system_settings')
        .update({
          key: setting.key,
          value: setting.value,
          category: setting.category,
          description: setting.description,
          is_public: setting.is_public,
          updated_at: new Date().toISOString()
        })
        .eq('id', setting.id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        systemSettings: prev.systemSettings.map(s => 
          s.id === setting.id ? { ...s, ...setting, updated_at: new Date().toISOString() } : s
        )
      }));
      
      toast.success('Setting updated successfully');
    } catch (error) {
      console.error('Error updating system setting:', error);
      toast.error('Failed to update setting');
      throw error;
    }
  };

  const addSystemSetting = async (setting: Omit<SystemSetting, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .insert([setting])
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No data returned');
      
      setState(prev => ({
        ...prev,
        systemSettings: [...prev.systemSettings, data[0]]
      }));
      
      toast.success('Setting added successfully');
    } catch (error) {
      console.error('Error adding system setting:', error);
      toast.error('Failed to add setting');
      throw error;
    }
  };

  const deleteSystemSetting = async (id: string) => {
    try {
      const { error } = await supabase
        .from('system_settings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        systemSettings: prev.systemSettings.filter(s => s.id !== id)
      }));
      
      toast.success('Setting deleted successfully');
    } catch (error) {
      console.error('Error deleting system setting:', error);
      toast.error('Failed to delete setting');
      throw error;
    }
  };

  // Contact Information CRUD operations
  const updateContactInformation = async (contact: ContactInformation) => {
    try {
      const { error } = await supabase
        .from('contact_information')
        .update({
          type: contact.type,
          name: contact.name,
          value: contact.value,
          is_primary: contact.is_primary,
          is_public: contact.is_public,
          updated_at: new Date().toISOString()
        })
        .eq('id', contact.id);

      if (error) throw error;
      
      // If this contact is set as primary, update other contacts of the same type
      if (contact.is_primary) {
        setState(prev => ({
          ...prev,
          contactInformation: prev.contactInformation.map(c => 
            c.id === contact.id ? 
              { ...c, ...contact, updated_at: new Date().toISOString() } : 
              c.type === contact.type ? 
                { ...c, is_primary: false } : 
                c
          )
        }));
      } else {
        setState(prev => ({
          ...prev,
          contactInformation: prev.contactInformation.map(c => 
            c.id === contact.id ? { ...c, ...contact, updated_at: new Date().toISOString() } : c
          )
        }));
      }
      
      toast.success('Contact information updated successfully');
    } catch (error) {
      console.error('Error updating contact information:', error);
      toast.error('Failed to update contact information');
      throw error;
    }
  };

  const addContactInformation = async (contact: Omit<ContactInformation, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('contact_information')
        .insert([contact])
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No data returned');
      
      // If this contact is set as primary, update other contacts of the same type
      if (contact.is_primary) {
        setState(prev => ({
          ...prev,
          contactInformation: [
            ...prev.contactInformation.map(c => 
              c.type === contact.type ? { ...c, is_primary: false } : c
            ),
            data[0]
          ]
        }));
      } else {
        setState(prev => ({
          ...prev,
          contactInformation: [...prev.contactInformation, data[0]]
        }));
      }
      
      toast.success('Contact information added successfully');
    } catch (error) {
      console.error('Error adding contact information:', error);
      toast.error('Failed to add contact information');
      throw error;
    }
  };

  const deleteContactInformation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contact_information')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        contactInformation: prev.contactInformation.filter(c => c.id !== id)
      }));
      
      toast.success('Contact information deleted successfully');
    } catch (error) {
      console.error('Error deleting contact information:', error);
      toast.error('Failed to delete contact information');
      throw error;
    }
  };

  // Bank Information CRUD operations
  const updateBankInformation = async (bank: BankInformation) => {
    try {
      const { error } = await supabase
        .from('bank_information')
        .update({
          bank_name: bank.bank_name,
          account_name: bank.account_name,
          account_number: bank.account_number,
          sort_code: bank.sort_code,
          swift_code: bank.swift_code,
          is_primary: bank.is_primary,
          is_public: bank.is_public,
          updated_at: new Date().toISOString()
        })
        .eq('id', bank.id);

      if (error) throw error;
      
      // If this bank is set as primary, update other banks
      if (bank.is_primary) {
        setState(prev => ({
          ...prev,
          bankInformation: prev.bankInformation.map(b => 
            b.id === bank.id ? 
              { ...b, ...bank, updated_at: new Date().toISOString() } : 
              { ...b, is_primary: false }
          )
        }));
      } else {
        setState(prev => ({
          ...prev,
          bankInformation: prev.bankInformation.map(b => 
            b.id === bank.id ? { ...b, ...bank, updated_at: new Date().toISOString() } : b
          )
        }));
      }
      
      toast.success('Bank information updated successfully');
    } catch (error) {
      console.error('Error updating bank information:', error);
      toast.error('Failed to update bank information');
      throw error;
    }
  };

  const addBankInformation = async (bank: Omit<BankInformation, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('bank_information')
        .insert([bank])
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No data returned');
      
      // If this bank is set as primary, update other banks
      if (bank.is_primary) {
        setState(prev => ({
          ...prev,
          bankInformation: [
            ...prev.bankInformation.map(b => ({ ...b, is_primary: false })),
            data[0]
          ]
        }));
      } else {
        setState(prev => ({
          ...prev,
          bankInformation: [...prev.bankInformation, data[0]]
        }));
      }
      
      toast.success('Bank information added successfully');
    } catch (error) {
      console.error('Error adding bank information:', error);
      toast.error('Failed to add bank information');
      throw error;
    }
  };

  const deleteBankInformation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('bank_information')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        bankInformation: prev.bankInformation.filter(b => b.id !== id)
      }));
      
      toast.success('Bank information deleted successfully');
    } catch (error) {
      console.error('Error deleting bank information:', error);
      toast.error('Failed to delete bank information');
      throw error;
    }
  };

  // User Roles CRUD operations
  const updateUserRole = async (role: UserRole) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({
          name: role.name,
          description: role.description,
          permissions: role.permissions,
          updated_at: new Date().toISOString()
        })
        .eq('id', role.id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        userRoles: prev.userRoles.map(r => 
          r.id === role.id ? { ...r, ...role, updated_at: new Date().toISOString() } : r
        )
      }));
      
      toast.success('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
      throw error;
    }
  };

  const addUserRole = async (role: Omit<UserRole, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .insert([role])
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No data returned');
      
      setState(prev => ({
        ...prev,
        userRoles: [...prev.userRoles, data[0]]
      }));
      
      toast.success('User role added successfully');
    } catch (error) {
      console.error('Error adding user role:', error);
      toast.error('Failed to add user role');
      throw error;
    }
  };

  const deleteUserRole = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        userRoles: prev.userRoles.filter(r => r.id !== id)
      }));
      
      toast.success('User role deleted successfully');
    } catch (error) {
      console.error('Error deleting user role:', error);
      toast.error('Failed to delete user role');
      throw error;
    }
  };

  // Communication Settings CRUD operations
  const updateCommunicationSetting = async (setting: CommunicationSetting) => {
    try {
      const { error } = await supabase
        .from('communication_settings')
        .update({
          type: setting.type,
          name: setting.name,
          subject: setting.subject,
          template: setting.template,
          placeholders: setting.placeholders,
          is_active: setting.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', setting.id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        communicationSettings: prev.communicationSettings.map(s => 
          s.id === setting.id ? { ...s, ...setting, updated_at: new Date().toISOString() } : s
        )
      }));
      
      toast.success('Communication setting updated successfully');
    } catch (error) {
      console.error('Error updating communication setting:', error);
      toast.error('Failed to update communication setting');
      throw error;
    }
  };

  const addCommunicationSetting = async (setting: Omit<CommunicationSetting, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('communication_settings')
        .insert([setting])
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No data returned');
      
      setState(prev => ({
        ...prev,
        communicationSettings: [...prev.communicationSettings, data[0]]
      }));
      
      toast.success('Communication setting added successfully');
    } catch (error) {
      console.error('Error adding communication setting:', error);
      toast.error('Failed to add communication setting');
      throw error;
    }
  };

  const deleteCommunicationSetting = async (id: string) => {
    try {
      const { error } = await supabase
        .from('communication_settings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        communicationSettings: prev.communicationSettings.filter(s => s.id !== id)
      }));
      
      toast.success('Communication setting deleted successfully');
    } catch (error) {
      console.error('Error deleting communication setting:', error);
      toast.error('Failed to delete communication setting');
      throw error;
    }
  };

  // Legal Documents CRUD operations
  const updateLegalDocument = async (document: LegalDocument) => {
    try {
      const { error } = await supabase
        .from('legal_documents')
        .update({
          type: document.type,
          title: document.title,
          content: document.content,
          version: document.version,
          is_active: document.is_active,
          effective_date: document.effective_date,
          updated_at: new Date().toISOString()
        })
        .eq('id', document.id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        legalDocuments: prev.legalDocuments.map(d => 
          d.id === document.id ? { ...d, ...document, updated_at: new Date().toISOString() } : d
        )
      }));
      
      toast.success('Legal document updated successfully');
    } catch (error) {
      console.error('Error updating legal document:', error);
      toast.error('Failed to update legal document');
      throw error;
    }
  };

  const addLegalDocument = async (document: Omit<LegalDocument, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .insert([document])
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No data returned');
      
      setState(prev => ({
        ...prev,
        legalDocuments: [...prev.legalDocuments, data[0]]
      }));
      
      toast.success('Legal document added successfully');
    } catch (error) {
      console.error('Error adding legal document:', error);
      toast.error('Failed to add legal document');
      throw error;
    }
  };

  const deleteLegalDocument = async (id: string) => {
    try {
      const { error } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setState(prev => ({
        ...prev,
        legalDocuments: prev.legalDocuments.filter(d => d.id !== id)
      }));
      
      toast.success('Legal document deleted successfully');
    } catch (error) {
      console.error('Error deleting legal document:', error);
      toast.error('Failed to delete legal document');
      throw error;
    }
  };

  const contextValue: SettingsContextType = {
    state,
    updateSystemSetting,
    addSystemSetting,
    deleteSystemSetting,
    updateContactInformation,
    addContactInformation,
    deleteContactInformation,
    updateBankInformation,
    addBankInformation,
    deleteBankInformation,
    updateUserRole,
    addUserRole,
    deleteUserRole,
    updateCommunicationSetting,
    addCommunicationSetting,
    deleteCommunicationSetting,
    updateLegalDocument,
    addLegalDocument,
    deleteLegalDocument,
    refreshSettings
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};